import React from "react";
import {
  RingProgress,
  Box,
  Text,
  Center,
  useMantineColorScheme,
} from "@mantine/core";
import "./rosette.scss";
import {
  formatName,
  generateSections,
  ROSETTE_ICONS,
  ROSETTE_OUTER_CIRCLE_COLORS,
} from "../ENERGY_BALANCE_CONFIG";
import { formatProjectNumber } from "../../../../utils/helpers";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../../../utils/atoms/projectDetailsAtom";
import { ReactComponent as PVIcon } from "../../../../assets/solar_panel1.svg";
import { ReactComponent as BatteryIcon } from "../../../../assets/battery2_general.svg";
import { ReactComponent as GensetIcon } from "../../../../assets/diesel_generator.svg";
import { ReactComponent as LoadIcon } from "../../../../assets/segment_building.svg";
import { ReactComponent as UtilityIcon } from "../../../../assets/segment_energy_infrastructure.svg";
import { ReactComponent as ElectricalIcon } from "../../../../assets/segment_electrical_energy.svg";

const EvcsIcon = ({ style }) => (
  <se-icon
    size="medium"
    style={{
      color: style?.fill,
      width: style?.width,
      height: style?.height,
      fontSize: style?.width,
    }}
  >
    electric_vehicle_terminal
  </se-icon>
);

const ICON_MAP = {
  pv: PVIcon,
  battery: BatteryIcon,
  genset: GensetIcon,
  load: LoadIcon,
  utility: UtilityIcon,
  evcs: EvcsIcon,
};

const Rosette = ({ data = {}, size = 100 }) => {
  const { colorScheme } = useMantineColorScheme();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const numberFormat = projectDetails?.projectDTO?.numberFormat;

  const allComponents = Object.keys(data?.componentsAttributes);
  const outerComponents = allComponents.filter((key) => key !== "load");
  outerComponents.push("load");

  const count = outerComponents.length;
  const outerDistance = 180;
  const containerSize = outerDistance * 3 + size;
  const center = containerSize / 2;
  const outerCircleRadius = size / 2;
  const centerCircleSize = size * 1.2;
  const centerCircleRadius = centerCircleSize / 2;

  const outerCircles = outerComponents.map((key, i) => {
    const angle = (2 * Math.PI * i) / count - Math.PI / 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const x = center + outerDistance * dx - outerCircleRadius;
    const y = center + outerDistance * dy - outerCircleRadius;

    const componentsAttributes = data?.componentsAttributes[key];
    const { props = {}, ...metrics } = componentsAttributes;
    const sections = generateSections(metrics, props);

    // Label positioning logic
    const labelOffset = 10;
    let labelLeft = center + outerDistance * dx;
    let labelTop = center + outerDistance * dy;
    let translateX = "-50%";
    let translateY = "-50%";

    if (angle >= -Math.PI / 4 && angle < Math.PI / 4) {
      labelLeft += outerCircleRadius + labelOffset;
      translateX = "0%";
    } else if (angle >= Math.PI / 4 && angle < (3 * Math.PI) / 4) {
      labelTop += outerCircleRadius + labelOffset;
      translateY = "0%";
    } else if (angle >= (3 * Math.PI) / 4 || angle < -(3 * Math.PI) / 4) {
      labelLeft -= outerCircleRadius + labelOffset;
      translateX = "-100%";
    } else {
      labelTop -= outerCircleRadius + labelOffset;
      translateY = "-100%";
    }

    return (
      <React.Fragment key={`rosette-circle-${key}`}>
        <Box className="rosette-circle" style={{ left: x, top: y }} >
          <RingProgress
            size={size}
            thickness={10}
            sections={sections}
            label={
              <Center>
                {(() => {
                  const IconComponent = ICON_MAP[props?.name?.toLowerCase()];
                  if (IconComponent) {
                    return (
                      <IconComponent
                        style={{
                          width: 32,
                          height: 32,
                          fill: colorScheme === "dark" ? "white" : "black",
                        }}
                      />
                    );
                  }
                  // fallback to se-icon if SVG not found
                  return <se-icon size="large">{ROSETTE_ICONS?.[props?.name]}</se-icon>;
                })()}
              </Center>
            }
          />
        </Box>
        <Box
          className="label-container"
          style={{
            left: labelLeft,
            top: labelTop,
            transform: `translate(${translateX}, ${translateY})`,
            zIndex: 3,
          }}
        >
          <Text
            size="sm"
            fw={700}
            color={`gray.${colorScheme === "dark" ? 0 : 7}`}
          >
            {props.name === "Battery" ? "BESS" : props.name || key}
          </Text>
          {Object.entries(metrics)?.map(([k, v]) => (
            <Text
              size="xs"
              color={`gray.${colorScheme === "dark" ? 0 : 7}`}
              key={k}
            >
              {formatName(k)} ( {formatProjectNumber(Number(v), { numberFormat })} MWh{k?.includes("thermal") ? <sub><i>th</i></sub> : null})
            </Text>
          ))}
        </Box>
      </React.Fragment>
    );
  });

  const lines = outerComponents.map((key, i) => {
    const angle = (2 * Math.PI * i) / count - Math.PI / 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const componentsAttributes = data?.componentsAttributes[key];
    const { props = {} } = componentsAttributes;
    const flowDirection = props.flowDirection || "forward";
    const startOffset = centerCircleRadius * 0.9;
    const endOffset = outerCircleRadius * 0.9;
    const startX = center + dx * startOffset;
    const startY = center + dy * startOffset;
    const endX = center + dx * (outerDistance - endOffset);
    const endY = center + dy * (outerDistance - endOffset);

    const [x1, y1, x2, y2] =
      flowDirection === "forward"
        ? [startX, startY, endX, endY]
        : [endX, endY, startX, startY];

    const length = Math.hypot(x2 - x1, y2 - y1);

    return (
      <line
        key={`line-${key}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={ROSETTE_OUTER_CIRCLE_COLORS[props?.name]}
        strokeDasharray={`${length / 10}, ${length / 10}`}
        strokeDashoffset={length}
        strokeWidth="3"
        className="animated-line"
      />
    );
  });

  return (
    <Box
      className="diagram-container"
      style={{ width: containerSize, height: containerSize , backgroundColor: colorScheme === "dark" ? "#111111" : "#fff" }}
    >
      <svg className="line-overlay">{lines}</svg>
      <Box
        className="rosette-circle center-circle"
        style={{
          left: center - centerCircleRadius,
          top: center - centerCircleRadius,
        }}
      >
        <RingProgress
          size={centerCircleSize}
          thickness={10}
          sections={[{ value: 100, color: "#dedede" }]}
          label={
            <Center>
              <ElectricalIcon
                width={32}
                height={32}
                fill={colorScheme === "dark" ? "white" : "black"}
              />
              {/* <se-icon size="large">segment_electrical_energy</se-icon> */}
            </Center>
          }
        />
      </Box>
      {outerCircles}
    </Box>
  );
};

export default Rosette;
